import React, { FormEvent, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ServerErrors } from 'dash-common';
import {
  Form,
  FormContext,
  FormErrorTypes,
  Modal,
  ModalContext,
  useBackendErrorMessage,
} from 'dash-ui';

import {
  GetOrganizationUsersByOrganizationRoleDocument,
  useCreateOrganizationUserMutation,
} from 'gql/generated.graphql';
import { OrganizationUsersModalsEnum } from 'pages/OrganizationPage/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/OrganizationUsers/constants/constants';

import { FormFields } from './constants/constants';
import useValidation from './hooks/useValidation';
import GeneralInputs from './components/GeneralInputs/GeneralInputs';
import RoleSelect from './components/RoleSelect/RoleSelect';
import PropertiesSelect from './components/PropertiesSelect/PropertiesSelect';

function SaveOrganizationUsersModal({
  organizationId,
}: {
  organizationId: string;
}) {
  const { isOpenCurrent, toggleModal, setPayload } = useContext(ModalContext);
  const isOpen =
    isOpenCurrent[OrganizationUsersModalsEnum.SAVE_ORGANIZATION_USER] || false;
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const translate = useBackendErrorMessage();
  const {
    formState,
    formOnSubmit,
    formOnError,
    resetForm,
    resetFormErrors,
    errorHandlers,
  } = useContext(FormContext);
  const valid = useValidation();

  const [createOrganizationUserMutation, { loading: createLoading }] =
    useCreateOrganizationUserMutation({
      refetchQueries: [GetOrganizationUsersByOrganizationRoleDocument],
    });

  const closeModal = () => {
    resetForm();
    resetFormErrors();
    toggleModal(false, OrganizationUsersModalsEnum.SAVE_ORGANIZATION_USER);
    setPayload(undefined);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { isLoading, errorSlug, ...formValues } = formState;
    const { firstName, lastName, email, role, properties } = formValues;

    if (
      !valid(FormFields.FirstName, firstName) ||
      !valid(FormFields.LastName, lastName) ||
      !valid(FormFields.Email, email) ||
      !valid(FormFields.Role, role?.type) ||
      !valid(FormFields.Properties, properties)
    ) {
      return null;
    }

    const propertyIds =
      properties?.map(({ _id }: { _id: string }) => _id) || [];
    const basicInput = {
      accountInput: {
        email,
      },
      roleId: role._id,
      profileInput: { firstName, lastName },
      ...(propertyIds.length ? { assignedPropertiesIds: propertyIds } : {}),
    };

    const onError = (error: { message: string }) => {
      if (
        [
          ServerErrors.ACCOUNT_EMAIL_ALREADY_EXIST as string,
          ServerErrors.ACCOUNT_ARCHIVED as string,
        ].includes(error?.message)
      ) {
        return errorHandlers[FormFields.Email]({
          type: FormErrorTypes.Invalid,
          message: t(`common:serverErrors:${error.message}`),
        });
      }
      return formOnError(translate(error));
    };

    return createOrganizationUserMutation({
      variables: { input: { ...basicInput, organizationId } },
      onCompleted: (data) => {
        closeModal();
        toggleModal(
          true,
          OrganizationUsersModalsEnum.SEND_REGISTRATION_LINK_TO_USER
        );
        setPayload(data.createOrganizationUser.account._id);
      },
      onError,
    });
  };

  const isLoading = createLoading;

  return (
    <Modal
      title={t('pages:organizationUsers.addOrganisationUserModalLabel') || ''}
      isOpen={isOpen}
      onRequestClose={closeModal}
      submitButton={{
        title: t('common:labels.add'),
        isLoading: isLoading,
        disabled: isLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t('common:labels.cancel'),
        onClick: closeModal,
      }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <GeneralInputs />
        <RoleSelect />
        <PropertiesSelect organizationId={organizationId} />
        <Form.Group mt={3}>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
}

export default SaveOrganizationUsersModal;
